/**
 * `date` → **Settings** (spec M1-B5 §6.2, M2 §5.5).
 *
 * Every key here exists in monday's `settings_str` and is round-tripped verbatim, including
 * the ones this product does not render yet (`show_week_number`, `show_weekends`) — an
 * unrecognised setting must survive a save, or a board loses configuration by being opened.
 */
import type { DateSettings } from '@elizmonday/shared';
import { useState } from 'react';
import type { ColumnSettingsProps } from '../types';

export function DateSettingsDialog({ settings, onCommit, onCancel }: ColumnSettingsProps<DateSettings>) {
  // Round 10i: `column.settings` is `null` on a column monday never configured.
  const [draft, setDraft] = useState<DateSettings>(() => ({ ...(settings ?? {}) }));
  const patch = (next: Partial<DateSettings>) => setDraft((current) => ({ ...current, ...next }));

  const toggle = (
    key: 'show_time_by_default' | 'show_week_day' | 'show_current_year' | 'use_numeric_only_format',
    label: string,
    testId: string,
  ) => (
    <label className="ez-column-settings__field" key={key}>
      <input
        type="checkbox"
        data-testid={testId}
        checked={draft[key] ?? false}
        onChange={(event) => patch({ [key]: event.target.checked } as Partial<DateSettings>)}
      />
      {label}
    </label>
  );

  return (
    <form
      className="ez-column-settings__form"
      data-testid="date-settings"
      onSubmit={(event) => {
        event.preventDefault();
        onCommit(draft);
      }}
    >
      <h2 className="ez-column-settings__title">Date settings</h2>

      {toggle('show_time_by_default', 'Include time', 'date-show-time')}
      {toggle('show_week_day', 'Show the weekday', 'date-show-weekday')}
      {toggle('show_current_year', 'Show the year even when it is this one', 'date-show-year')}
      {toggle('use_numeric_only_format', 'Numeric format (2026-09-10)', 'date-numeric')}

      <label className="ez-column-settings__field">
        Group summary
        <select
          data-testid="date-calc-type"
          value={draft.calcType ?? 'earliestToLatest'}
          onChange={(event) => patch({ calcType: event.target.value as DateSettings['calcType'] })}
        >
          <option value="earliestToLatest">Earliest → latest</option>
          <option value="earliest">Earliest</option>
          <option value="latest">Latest</option>
        </select>
      </label>

      <footer className="ez-column-settings__footer">
        <button type="button" className="ez-column-settings__cancel" data-testid="settings-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="ez-column-settings__save" data-testid="settings-save">
          Apply
        </button>
      </footer>
    </form>
  );
}
